<template>
  <div class="tier-reason-report-container">
    <FiltersWrapper>
      <CollapseBlock label="Фильтры" :alwaysHideExpand="true" className="reason-report-filters-collapse">
        <div class="filters-content">
          <div class="filters-row">
            <div class="filter-item">
              <CustomSelect
                :options="residents"
                label="Жилой комплекс"
                optionValueKey="resident_id"
                optionLabelKey="resident_name"
                :value="filters.resident_id"
                :loading="residentsFetching"
                @change="onResidentChange"
                clearable
              />
            </div>
            <div class="filter-item">
              <CustomSelect
                :options="blocks"
                label="Блок"
                optionValueKey="block_id"
                optionLabelKey="block_name"
                :value="filters.block_id"
                :loading="blocksFetching"
                :disabled="!filters.resident_id"
                @change="onBlockChange"
                clearable
              />
            </div>
            <div class="filter-item date-filter">
              <div class="date-inputs">
                <q-input
                  v-model="filters.date_begin"
                  type="date"
                  outlined
                  dense
                  class="date-input"
                  @update:model-value="onDateChange"
                />
                <span class="date-separator">—</span>
                <q-input
                  v-model="filters.date_end"
                  type="date"
                  outlined
                  dense
                  class="date-input"
                  @update:model-value="onDateChange"
                />
              </div>
            </div>
            <div class="filter-actions">
              <q-btn
                color="primary"
                icon="search"
                label="Показать"
                @click="loadReportData"
                :loading="loading"
                unelevated
              />
              <q-btn
                flat
                color="grey-7"
                icon="clear"
                label="Очистить"
                @click="clearFilters"
              />
            </div>
          </div>
        </div>
      </CollapseBlock>
    </FiltersWrapper>

    <div class="report-content">
      <div v-if="!dataLoaded && !loading" class="no-data-state">
        <q-icon name="info_outline" size="3em" color="grey-5" />
        <div class="no-data-text">Выберите фильтры и нажмите "Показать"</div>
      </div>

      <div v-else-if="loading" class="loading-state">
        <q-spinner color="primary" size="3em" />
        <div class="loading-text">Загрузка данных...</div>
      </div>

      <div v-else-if="reportData.length === 0" class="no-data-state">
        <q-icon name="search_off" size="3em" color="grey-5" />
        <div class="no-data-text">Нет данных для отображения</div>
      </div>

      <div v-else class="report-wrapper">
        <div class="summary-and-controls">
          <div class="summary-section">
            <div class="summary-item">
              <q-icon name="assignment" size="1.3em" />
              <span class="summary-label">Всего:</span>
              <span class="summary-value">{{ displayData.length }}</span>
            </div>
            <div class="summary-item highlight">
              <q-icon name="error_outline" size="1.3em" />
              <span class="summary-label">Отставаний:</span>
              <span class="summary-value">{{ totalCount }}</span>
            </div>
          </div>

          <div class="controls-group">
            <div class="grouping-controls">
              <span class="grouping-label">Группировка:</span>
              <q-btn-toggle
                v-model="groupBy"
                :options="[
                  { label: 'Группа работ', value: 'work_group', icon: 'view_list' },
                  { label: 'Блок', value: 'block', icon: 'domain' },
                  { label: 'ЖК', value: 'project', icon: 'apartment' },
                ]"
                toggle-color="primary"
                unelevated
                dense
                class="grouping-toggle"
              />
            </div>

            <q-btn
              color="positive"
              icon="file_download"
              label="Экспорт в Excel"
              @click="exportToExcel"
              :disable="reportData.length === 0"
              :loading="exporting"
              unelevated
              class="export-btn"
            />
          </div>
        </div>

        <div class="table-wrapper">
          <table class="report-table">
            <thead>
              <tr>
                <th class="col-number">№</th>
                <th v-if="groupBy === 'work_group'" class="col-context">ЖК / Блок / Группа работ</th>
                <th v-else-if="groupBy === 'block'" class="col-context-grouped">ЖК / Блок</th>
                <th v-else-if="groupBy === 'project'" class="col-context-simple">ЖК</th>
                <th class="col-reason">Причина</th>
                <th v-if="groupBy === 'work_group'" class="col-date">Дата</th>
                <th class="col-count">Кол.</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in displayData" :key="index" :class="getRowClass(index)">
                <td class="col-number">{{ index + 1 }}</td>
                
                <!-- Детальная группировка -->
                <td v-if="groupBy === 'work_group'" class="col-context">
                  <div class="context-stack">
                    <div class="context-project">{{ row.sbs_project_name || '—' }}</div>
                    <div class="context-details">
                      <span class="context-block">{{ row.block_name || '—' }}</span>
                      <span class="context-separator">•</span>
                      <span class="context-work-group">{{ row.work_group_name || '—' }}</span>
                    </div>
                  </div>
                </td>

                <!-- Группировка по блоку -->
                <td v-else-if="groupBy === 'block'" class="col-context-grouped">
                  <div class="context-stack">
                    <div class="context-project">{{ row.sbs_project_name || '—' }}</div>
                    <div class="context-block-only">{{ row.block_name || '—' }}</div>
                  </div>
                </td>

                <!-- Группировка по проекту -->
                <td v-else-if="groupBy === 'project'" class="col-context-simple">
                  <div class="context-project">{{ row.sbs_project_name || '—' }}</div>
                </td>

                <td class="col-reason">
                  <span class="reason-text">{{ row.reason_name || '—' }}</span>
                </td>

                <td v-if="groupBy === 'work_group'" class="col-date">{{ formatDate(row.fact_date) }}</td>

                <td class="col-count">
                  <span class="count-badge" :class="getCountBadgeClass(row.cnt)">{{ row.cnt }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomSelect from "@/components/form/CustomSelect";
import CollapseBlock from "@/components/common/CollapseBlock";
import FiltersWrapper from "@/components/common/FiltersWrapper";
import { getResidents, getBlocks } from "@/views/TierPlan/services";
import { getWorkTaskFactReasonReport } from "@/views/TierFactTimelineTier2to6/services";
import { downloadFile } from "@/services";

export default {
  name: "TierReasonReport",
  components: { CustomSelect, CollapseBlock, FiltersWrapper },
  data() {
    return {
      residents: [],
      residentsFetching: false,
      blocks: [],
      blocksFetching: false,
      filters: {
        resident_id: null,
        block_id: null,
        date_begin: null,
        date_end: null,
      },
      reportData: [],
      loading: false,
      dataLoaded: false,
      exporting: false,
      groupBy: 'work_group', // 'work_group', 'block', 'project'
    };
  },
  computed: {
    totalCount() {
      return this.reportData.reduce((sum, row) => sum + (row.cnt || 0), 0);
    },
    displayData() {
      if (this.groupBy === 'work_group') {
        // Без группировки - показываем все данные как есть
        return this.reportData;
      } else if (this.groupBy === 'block') {
        // Группировка по ЖК + Блок
        const groups = {};
        this.reportData.forEach(row => {
          const key = `${row.sbs_project_name}_${row.block_name}_${row.reason_name}`;
          if (!groups[key]) {
            groups[key] = {
              sbs_project_name: row.sbs_project_name,
              block_name: row.block_name,
              work_group_name: null,
              reason_name: row.reason_name,
              fact_date: null,
              cnt: 0
            };
          }
          groups[key].cnt += row.cnt || 0;
        });
        return Object.values(groups);
      } else if (this.groupBy === 'project') {
        // Группировка только по ЖК
        const groups = {};
        this.reportData.forEach(row => {
          const key = `${row.sbs_project_name}_${row.reason_name}`;
          if (!groups[key]) {
            groups[key] = {
              sbs_project_name: row.sbs_project_name,
              block_name: null,
              work_group_name: null,
              reason_name: row.reason_name,
              fact_date: null,
              cnt: 0
            };
          }
          groups[key].cnt += row.cnt || 0;
        });
        return Object.values(groups);
      }
      return this.reportData;
    },
  },
  async mounted() {
    await this.loadResidents();
    this.loadFiltersFromQuery();
  },
  methods: {
    async loadResidents() {
      this.residentsFetching = true;
      try {
        this.residents = await getResidents();
      } catch (e) {
        console.error("Error loading residents:", e);
        this.residents = [];
      } finally {
        this.residentsFetching = false;
      }
    },
    async loadBlocks(residentId) {
      if (!residentId) {
        this.blocks = [];
        return;
      }
      this.blocksFetching = true;
      try {
        this.blocks = await getBlocks(residentId);
      } catch (e) {
        console.error("Error loading blocks:", e);
        this.blocks = [];
      } finally {
        this.blocksFetching = false;
      }
    },
    async onResidentChange(value) {
      this.filters.resident_id = value;
      this.filters.block_id = null;
      this.blocks = [];
      if (value) {
        await this.loadBlocks(value);
      }
      this.updateQuery();
    },
    onBlockChange(value) {
      this.filters.block_id = value;
      this.updateQuery();
    },
    onDateChange() {
      this.updateQuery();
    },
    async loadReportData() {
      this.loading = true;
      try {
        this.reportData = await getWorkTaskFactReasonReport(
          this.filters.resident_id,
          this.filters.block_id,
          this.filters.date_begin,
          this.filters.date_end
        );
        this.dataLoaded = true;
      } catch (e) {
        console.error("Error loading report data:", e);
        this.reportData = [];
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.filters = {
        resident_id: null,
        block_id: null,
        date_begin: null,
        date_end: null,
      };
      this.blocks = [];
      this.reportData = [];
      this.dataLoaded = false;
      this.updateQuery();
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString("ru-RU", {
          day: "2-digit",
          month: "2-digit",
        });
      } catch (e) {
        return dateStr;
      }
    },
    getRowClass(index) {
      return index % 2 === 0 ? 'row-even' : 'row-odd';
    },
    getCountBadgeClass(cnt) {
      if (cnt >= 5) return 'badge-critical';
      if (cnt >= 3) return 'badge-high';
      return 'badge-medium';
    },
    updateQuery() {
      const query = {};
      if (this.filters.resident_id) query.resident_id = this.filters.resident_id;
      if (this.filters.block_id) query.block_id = this.filters.block_id;
      if (this.filters.date_begin) query.date_begin = this.filters.date_begin;
      if (this.filters.date_end) query.date_end = this.filters.date_end;
      this.$router.replace({ query }).catch(() => {});
    },
    async loadFiltersFromQuery() {
      const { resident_id, block_id, date_begin, date_end } = this.$route.query;
      
      if (resident_id) {
        this.filters.resident_id = parseInt(resident_id);
        await this.loadBlocks(this.filters.resident_id);
      }
      
      if (block_id && this.filters.resident_id) {
        this.filters.block_id = parseInt(block_id);
      }
      
      if (date_begin) {
        this.filters.date_begin = date_begin;
      }
      
      if (date_end) {
        this.filters.date_end = date_end;
      }
      
      // Если есть фильтры в URL, автоматически загружаем данные
      if (resident_id || block_id || date_begin || date_end) {
        await this.loadReportData();
      }
    },
    async exportToExcel() {
      if (!this.reportData || this.reportData.length === 0) {
        this.$q.notify({
          type: 'warning',
          message: 'Нет данных для экспорта',
          position: 'top',
        });
        return;
      }

      this.exporting = true;
      try {
        const params = {};
        if (this.filters.resident_id) params.sbs_project_id = this.filters.resident_id;
        if (this.filters.block_id) params.block_id = this.filters.block_id;
        if (this.filters.date_begin) params.date_begin = this.filters.date_begin;
        if (this.filters.date_end) params.date_end = this.filters.date_end;

        await downloadFile(
          '/tier/work_task_fact_reason_report/export/',
          'Отчет_по_причинам_отставания',
          params,
          'xlsx',
          'get',
          true
        );

        this.$q.notify({
          type: 'positive',
          message: 'Файл успешно экспортирован',
          position: 'top',
        });
      } catch (error) {
        console.error('Export error:', error);
        this.$q.notify({
          type: 'negative',
          message: 'Ошибка при экспорте файла',
          position: 'top',
        });
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.tier-reason-report-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px;
}

.reason-report-filters-collapse {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filters-content {
  padding: 8px 0;
}

.filters-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: flex-end;
}

.filter-item {
  flex: 1;
  min-width: 220px;
}

.date-filter {
  min-width: 280px;
}

.date-inputs {
  display: flex;
  gap: 12px;
  align-items: center;
}

.date-input {
  flex: 1;
}

.date-separator {
  color: #9ca3af;
  font-size: 14px;
  margin: 0 4px;
}

.filter-actions {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.report-content {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 400px;
}

.loading-state,
.no-data-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 400px;
}

.loading-text,
.no-data-text {
  font-size: 16px;
  color: #6b7280;
}

.report-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.summary-and-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.summary-section {
  display: flex;
  gap: 16px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 6px;
  border-left: 3px solid #0284c7;
}

.controls-group {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.grouping-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.grouping-label {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}

.grouping-toggle {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.export-btn {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  font-weight: 600;
}

.summary-item {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 4px 12px;
  background: white;
  border-radius: 4px;
}

.summary-item.highlight {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
}

.summary-item .q-icon {
  color: #0284c7;
}

.summary-item.highlight .q-icon {
  color: #dc2626;
}

.summary-label {
  font-weight: 600;
  color: #374151;
  font-size: 13px;
}

.summary-value {
  font-size: 18px;
  font-weight: 700;
  color: #0284c7;
}

.summary-item.highlight .summary-value {
  color: #dc2626;
}

.table-wrapper {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: visible;
}

.report-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  background: #fff;
}

.report-table thead {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
}

.report-table th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 2px solid #0c4a6e;
}

.report-table th:first-child {
  border-top-left-radius: 6px;
}

.report-table th:last-child {
  border-top-right-radius: 6px;
}

.report-table td {
  padding: 8px 12px;
  color: #1f2937;
  border-bottom: 1px solid #e5e7eb;
}

.report-table tbody tr {
  transition: all 0.15s ease;
}

.report-table tbody tr.row-even {
  background: #ffffff;
}

.report-table tbody tr.row-odd {
  background: #f9fafb;
}

.report-table tbody tr:hover {
  background: #eff6ff !important;
  transform: scale(1.001);
  box-shadow: 0 2px 8px rgba(2, 132, 199, 0.1);
}

.col-number {
  width: 50px;
  text-align: center !important;
  font-weight: 600;
  color: #0284c7;
  font-size: 12px;
}

.col-context {
  min-width: 300px;
  max-width: 400px;
}

.context-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.context-project {
  font-weight: 600;
  color: #0369a1;
  font-size: 13px;
}

.context-details {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
}

.context-block {
  font-weight: 500;
  color: #374151;
}

.context-separator {
  color: #d1d5db;
}

.context-work-group {
  color: #6b7280;
  font-style: italic;
}

.col-reason {
  min-width: 250px;
  max-width: 400px;
}

.reason-text {
  display: inline-block;
  padding: 4px 10px;
  background: #fef3c7;
  border-left: 3px solid #f59e0b;
  border-radius: 3px;
  color: #92400e;
  font-weight: 500;
  font-size: 13px;
}

.col-date {
  width: 80px;
  text-align: center !important;
  font-weight: 600;
  color: #6b7280;
  font-size: 12px;
}

.col-count {
  width: 60px;
  text-align: center !important;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 24px;
  padding: 0 8px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.count-badge.badge-medium {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #fff;
}

.count-badge.badge-high {
  background: linear-gradient(135deg, #fb923c 0%, #f97316 100%);
  color: #fff;
}

.count-badge.badge-critical {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: #fff;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  50% {
    box-shadow: 0 2px 8px rgba(220, 38, 38, 0.5);
  }
}

.col-context-grouped {
  min-width: 200px;
}

.col-context-simple {
  min-width: 150px;
}

.context-block-only {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  margin-top: 4px;
}

@media (max-width: 1200px) {
  .filters-row {
    flex-direction: column;
  }

  .filter-item,
  .date-filter {
    width: 100%;
    min-width: 100%;
  }

  .filter-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .summary-and-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .summary-section {
    flex-direction: column;
    align-items: stretch;
  }

  .controls-group {
    flex-direction: column;
    align-items: stretch;
  }

  .grouping-controls {
    justify-content: space-between;
  }

  .export-btn {
    width: 100%;
  }
}
</style>
